import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { auth } from "@/lib/api";
import { TickProvider } from "@/lib/ticks";

// Eager: entry + shells + the two most-visited landing pages.
// Loaded synchronously so first paint is instant.
import Login from "@/pages/Login";
import Layout from "@/components/Layout";
import MobileLayout from "@/components/MobileLayout";
import Dashboard from "@/pages/Dashboard";
import AdminDashboard from "@/pages/AdminDashboard";

// Lazy: every other route. Each becomes its own webpack chunk so the initial
// bundle drops ~30-40% (admin-only code isn't shipped to clients, rarely-used
// pages don't block first paint). Cached after first visit — second nav is instant.
const PendingOrders = lazy(() => import("@/pages/PendingOrders"));
const TradeBook = lazy(() => import("@/pages/TradeBook"));
const Positions = lazy(() => import("@/pages/Positions"));
const Ledger = lazy(() => import("@/pages/Ledger"));
const Profile = lazy(() => import("@/pages/Profile"));

const AdminClients = lazy(() => import("@/pages/AdminClients"));
const AdminClientDetail = lazy(() => import("@/pages/AdminClientDetail"));
const AdminBroadcast = lazy(() => import("@/pages/AdminBroadcast"));
const AdminBans = lazy(() => import("@/pages/AdminBans"));
const AdminClosingPrices = lazy(() => import("@/pages/AdminClosingPrices"));
const AdminClosingPriceAudit = lazy(() => import("@/pages/AdminClosingPriceAudit"));
const AdminMarketWatch = lazy(() => import("@/pages/AdminMarketWatch"));
const AdminTrades = lazy(() => import("@/pages/AdminTrades"));
const AdminPositions = lazy(() => import("@/pages/AdminPositions"));
const AdminPending = lazy(() => import("@/pages/AdminPending"));
const AdminVwap = lazy(() => import("@/pages/AdminVwap"));
const AdminAuditLog = lazy(() => import("@/pages/AdminAuditLog"));
const AdminLeaderboard = lazy(() => import("@/pages/AdminLeaderboard"));
const AdminSystem = lazy(() => import("@/pages/AdminSystem"));
const AdminMarketCalendar = lazy(() => import("@/pages/AdminMarketCalendar"));
const AdminUcLcAudit = lazy(() => import("@/pages/AdminUcLcAudit"));
const AdminActivityLog = lazy(() => import("@/pages/AdminActivityLog"));
const AdminSettlements = lazy(() => import("@/pages/AdminSettlements"));
const AdminBulk = lazy(() => import("@/pages/AdminBulk"));
const AdminDataPurge = lazy(() => import("@/pages/AdminDataPurge"));

// Tiny inline fallback — page shells render immediately, just the route body
// shows this while its chunk loads (typically <200ms on broadband).
function RouteFallback() {
  return (
    <div className="flex items-center justify-center min-h-[40vh] text-xs text-muted-foreground"
         data-testid="route-loading">
      <div className="flex items-center gap-2">
        <span className="inline-block h-2 w-2 rounded-full bg-primary animate-pulse" />
        Loading…
      </div>
    </div>
  );
}

// Wraps a lazy page in <Suspense> so only the route body shows a fallback —
// Layout / MobileLayout shells stay mounted across nav transitions.
const L = (PageEl) => (
  <Suspense fallback={<RouteFallback />}>{PageEl}</Suspense>
);


function Guard({ role, children }) {
  const u = auth.getUser();
  if (!u) return <Navigate to="/login" replace />;
  if (role && u.role !== role) return <Navigate to={u.role === "admin" ? "/admin" : "/dashboard"} replace />;
  // TickProvider must mount AFTER login so REST seed + WS connect run with a valid token.
  return <TickProvider>{children}</TickProvider>;
}

function Root() {
  const u = auth.getUser();
  // OAuth safety net: if Shoonya redirected to "/" instead of "/api/oauth/callback"
  // (because the whitelisted URL is the bare domain), forward the auth_code to
  // the backend so admin doesn't have to copy-paste it manually.
  if (typeof window !== "undefined") {
    const sp = new URLSearchParams(window.location.search);
    const code = sp.get("code");
    if (code) {
      // Send code → backend exchange endpoint, then route to /admin/system
      // We use plain fetch (no axios deps) to avoid the auth-token requirement —
      // backend's /api/oauth/callback accepts the public Shoonya callback.
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/oauth/callback?code=${encodeURIComponent(code)}`;
      window.location.replace(url);
      return null;
    }
  }
  if (!u) return <Navigate to="/login" replace />;
  return <Navigate to={u.role === "admin" ? "/admin" : "/dashboard"} replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/" element={<Root />} />

        {/* Client (mobile-first layout) */}
        <Route element={<Guard role="client"><MobileLayout /></Guard>}>
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/orders" element={L(<PendingOrders />)} />
          <Route path="/tradebook" element={L(<TradeBook />)} />
          <Route path="/positions" element={L(<Positions />)} />
          <Route path="/ledger" element={L(<Ledger />)} />
          <Route path="/profile" element={L(<Profile />)} />
        </Route>

        {/* Admin */}
        <Route element={<Guard role="admin"><Layout /></Guard>}>
          <Route path="/admin" element={<AdminDashboard />} />
          <Route path="/admin/marketwatch" element={L(<AdminMarketWatch />)} />
          <Route path="/admin/clients" element={L(<AdminClients />)} />
          <Route path="/admin/clients/:cid" element={L(<AdminClientDetail />)} />
          <Route path="/admin/broadcast" element={L(<AdminBroadcast />)} />
          <Route path="/admin/bans" element={L(<AdminBans />)} />
          <Route path="/admin/closing-prices" element={L(<AdminClosingPrices />)} />
          <Route path="/admin/closing-prices-audit" element={L(<AdminClosingPriceAudit />)} />
          <Route path="/admin/trades" element={L(<AdminTrades />)} />
          <Route path="/admin/positions" element={L(<AdminPositions />)} />
          <Route path="/admin/pending" element={L(<AdminPending />)} />
          <Route path="/admin/vwap" element={L(<AdminVwap />)} />
          <Route path="/admin/leaderboard" element={L(<AdminLeaderboard />)} />
          <Route path="/admin/audit" element={L(<AdminAuditLog />)} />
          <Route path="/admin/system" element={L(<AdminSystem />)} />
          <Route path="/admin/market-calendar" element={L(<AdminMarketCalendar />)} />
          <Route path="/admin/uc-lc" element={L(<AdminUcLcAudit />)} />
          <Route path="/admin/activity-logs" element={L(<AdminActivityLog />)} />
          <Route path="/admin/settlements" element={L(<AdminSettlements />)} />
          <Route path="/admin/bulk" element={L(<AdminBulk />)} />
          <Route path="/admin/data-purge" element={L(<AdminDataPurge />)} />
        </Route>

        <Route path="*" element={<Root />} />
      </Routes>
      <Toaster richColors position="top-right" />
    </BrowserRouter>
  );
}
